<script setup lang="ts">
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { isEnrichedShift, type RouteAssignment, type ExistingShiftStep } from '@/api/route-plans';
import {
  assignmentStepKey, existingShiftStepKey, buildTravelGaps, type TravelGap,
} from '@/utils/route-plan-merged-steps';
import { LUNCH_DURATION_OPTIONS } from './route-plan-setup.utils';

const { t } = useI18n();

interface TimeBlock {
  startTime: string;
  endTime: string;
}

interface Props {
  assignmentsByCaregiver: Record<number, { name: string; assignments: RouteAssignment[] }>;
  colorMap: Record<number, string>;
  selectedCaregiverId: number | null;
  caregiverBreaks: Record<number, TimeBlock>;
  caregiverExistingShifts: Record<number, ExistingShiftStep[]>;
}

const props = defineProps<Props>();
const emit = defineEmits<{
  'select-caregiver': [id: number];
}>();

const HOURS_START = 8;
const MIN_HOURS_END = 21;
const HOURS_IN_DAY = 24;
const MINUTES_PER_HOUR = 60;
const SECONDS_PER_MINUTE = 60;
const PERCENTAGE = 100;
const PAD_LENGTH = 2;
const DAY_START = HOURS_START * MINUTES_PER_HOUR;
const DEFAULT_TOTAL_MINUTES = (MIN_HOURS_END - HOURS_START) * MINUTES_PER_HOUR;

const caregiverEntries = computed(() =>
  Object.entries(props.assignmentsByCaregiver).map(([id, group]) => ({ cgId: Number(id), group })),
);

function timeToMinutes(timeStr: string): number {
  const d = new Date(timeStr);
  if (Number.isNaN(d.getTime())) return 0;

  return d.getHours() * MINUTES_PER_HOUR + d.getMinutes();
}

const lastEndMinutes = computed(() => {
  const ends: number[] = [];
  Object.values(props.assignmentsByCaregiver).forEach(({ assignments }) =>
    assignments.forEach(a => ends.push(timeToMinutes(a.departureTime))));
  Object.values(props.caregiverExistingShifts).forEach(shifts =>
    shifts.forEach(s => ends.push(timeToMinutes(s.endTime))));
  Object.values(props.caregiverBreaks).forEach(b => ends.push(timeToMinutes(b.endTime)));

  return Math.max(0, ...ends);
});

// The axis ends at 21:00 or at the last block's end rounded up to the next hour,
// whichever is later (e.g. a visit until 23:30 extends it to 24:00).
const hoursEnd = computed(() => Math.min(
  HOURS_IN_DAY,
  Math.max(MIN_HOURS_END, Math.ceil(lastEndMinutes.value / MINUTES_PER_HOUR)),
));

const totalMinutes = computed(() => (hoursEnd.value * MINUTES_PER_HOUR) - DAY_START);

// Extra hours widen the rows past the container (horizontal scroll) instead of
// compressing the blocks; 1 means the default 08:00-21:00 full-width layout.
const extendRatio = computed(() => totalMinutes.value / DEFAULT_TOTAL_MINUTES);

const hours = computed(() => {
  const result: string[] = [];
  for (let h = HOURS_START; h <= hoursEnd.value; h++) {
    result.push(`${String(h).padStart(PAD_LENGTH, '0')}:00`);
  }

  return result;
});

function blockLeft(timeStr: string): number {
  const mins = timeToMinutes(timeStr);

  return ((mins - DAY_START) / totalMinutes.value) * PERCENTAGE;
}

function blockWidth(startStr: string, endStr: string): number {
  return ((timeToMinutes(endStr) - timeToMinutes(startStr)) / totalMinutes.value) * PERCENTAGE;
}

function patientInitials(fullName: string): string {
  const parts = fullName.trim().split(/\s+/);
  const first = parts[0]?.[0] ?? '';
  const last = parts[parts.length - 1]?.[0] ?? '';

  return (first + last).toUpperCase();
}

function gapStyle(gap: TravelGap | null) {
  if (!gap) return null;

  return {
    left: `${((gap.startMin - DAY_START) / totalMinutes.value) * PERCENTAGE}%`,
    width: `${((gap.endMin - gap.startMin) / totalMinutes.value) * PERCENTAGE}%`,
  };
}

function timeBlockStyle(block: TimeBlock) {
  const startMin = timeToMinutes(block.startTime);
  const endMin = timeToMinutes(block.endTime);

  return {
    left: `${((startMin - DAY_START) / totalMinutes.value) * PERCENTAGE}%`,
    width: `${((endMin - startMin) / totalMinutes.value) * PERCENTAGE}%`,
  };
}

function getBreakStyle(caregiverId: number) {
  const brk = props.caregiverBreaks[caregiverId];
  if (!brk) return null;

  return timeBlockStyle(brk);
}

function formatMinutes(mins: number): string {
  const h = Math.floor(mins / MINUTES_PER_HOUR);
  const m = Math.round(mins % MINUTES_PER_HOUR);

  return h > 0 ? `${h}h ${m}m` : `${m} min`;
}

// "30 min" / "1 h" via the shared toggle labels; any other length (legacy data)
// falls back to the generic minutes formatting.
function breakDurationLabel(caregiverId: number): string {
  const brk = props.caregiverBreaks[caregiverId];
  if (!brk) return '';

  const minutes = timeToMinutes(brk.endTime) - timeToMinutes(brk.startTime);

  return (LUNCH_DURATION_OPTIONS as readonly number[]).includes(minutes) ?
    t(`routePlan.lunchDuration.option${minutes}`) :
    formatMinutes(minutes);
}

function getExistingShifts(caregiverId: number): ExistingShiftStep[] {
  return props.caregiverExistingShifts[caregiverId] || [];
}

// Memoized travel gaps for all steps per caregiver, keyed by stable step key.
const travelGapsByCaregiver = computed(() => {
  const result: Record<number, Map<string, TravelGap>> = {};
  for (const cgIdStr of Object.keys(props.assignmentsByCaregiver)) {
    const cgId = Number(cgIdStr);
    result[cgId] = buildTravelGaps(
      props.assignmentsByCaregiver[cgId]?.assignments ?? [], getExistingShifts(cgId),
    );
  }

  return result;
});

function getStepTravelGap(stepKey: string, caregiverId: number): TravelGap | null {
  return travelGapsByCaregiver.value[caregiverId]?.get(stepKey) ?? null;
}

function travelTooltipText(
  step: RouteAssignment | ExistingShiftStep, stepKey: string, caregiverId: number,
): string {
  const gap = getStepTravelGap(stepKey, caregiverId);
  if (!gap) return '';

  const gapMins = gap.endMin - gap.startMin;
  const lines = [`${t('routePlan.totalWait')}: ${formatMinutes(gapMins)}`];
  if (gap.humanTravelTime) lines.push(`${t('routePlan.travelTime')}: ${gap.humanTravelTime}`);

  if ('travelTime' in step && step.travelTime) {
    const extraMins = gapMins - (step.travelTime / SECONDS_PER_MINUTE);
    if (extraMins > 1) lines.push(`${t('routePlan.extraWait')}: ${formatMinutes(extraMins)}`);
  }

  return lines.join('\n');
}

function tooltipText(assignment: RouteAssignment): string {
  const lines = [
    assignment.patientFullName,
    `${assignment.humanArrivalTime} - ${assignment.humanDepartureTime}`,
  ];
  if (assignment.humanTravelTime) {
    lines.push(`${t('routePlan.travelTime')}: ${assignment.humanTravelTime}`);
  }
  if (assignment.manual) lines.push(t('routePlan.manualPlacement'));

  return lines.join('\n');
}
</script>

<template>
  <div
    class="route-timeline"
    :style="{ '--timeline-extend-ratio': extendRatio }"
  >
    <div class="timeline-header">
      <div class="timeline-label" />
      <div class="timeline-hours">
        <span
          v-for="hour in hours"
          :key="hour"
        >{{ hour }}</span>
      </div>
    </div>

    <div
      v-for="{ cgId, group } in caregiverEntries"
      :key="cgId"
      class="timeline-row"
      :class="{ 'timeline-row--selected': selectedCaregiverId === cgId }"
      @click="emit('select-caregiver', cgId)"
    >
      <div class="timeline-label">
        {{ group.name }}
      </div>
      <div class="timeline-track">
        <!-- Existing shifts -->
        <template
          v-for="(shift, sIdx) in getExistingShifts(cgId)"
          :key="`existing-${sIdx}`"
        >
          <v-tooltip
            v-if="gapStyle(getStepTravelGap(existingShiftStepKey(shift, sIdx), cgId))"
            location="top"
          >
            <template #activator="{ props: tooltipProps }">
              <div
                class="timeline-block timeline-block--travel"
                :style="gapStyle(getStepTravelGap(existingShiftStepKey(shift, sIdx), cgId))!"
                v-bind="tooltipProps"
              />
            </template>
            <span class="tooltip-text">
              {{ travelTooltipText(shift, existingShiftStepKey(shift, sIdx), cgId) }}
            </span>
          </v-tooltip>

          <v-tooltip location="top">
            <template #activator="{ props: tooltipProps }">
              <div
                class="timeline-block timeline-block--existing"
                :class="{ 'timeline-block--dropped': isEnrichedShift(shift) && shift.dropped }"
                :style="timeBlockStyle(shift)"
                v-bind="tooltipProps"
              />
            </template>
            <span class="tooltip-text">
              {{ isEnrichedShift(shift) ? shift.patientFullName : t('routePlan.existingShift') }}
              <template v-if="isEnrichedShift(shift)">
                <br>{{ shift.humanStartTime }} - {{ shift.humanEndTime }}
              </template>
              <br>{{ t('routePlan.existingShift') }}
              <template v-if="isEnrichedShift(shift) && shift.dropped">
                <br>{{ t('routePlan.notOptimized') }}
              </template>
            </span>
          </v-tooltip>
        </template>

        <template
          v-for="assignment in group.assignments"
          :key="assignment.id"
        >
          <v-tooltip
            v-if="gapStyle(getStepTravelGap(assignmentStepKey(assignment), cgId))"
            location="top"
          >
            <template #activator="{ props: tooltipProps }">
              <div
                class="timeline-block timeline-block--travel"
                :style="gapStyle(getStepTravelGap(assignmentStepKey(assignment), cgId))!"
                v-bind="tooltipProps"
              />
            </template>
            <span class="tooltip-text">
              {{ travelTooltipText(assignment, assignmentStepKey(assignment), cgId) }}
            </span>
          </v-tooltip>

          <v-tooltip location="top">
            <template #activator="{ props: tooltipProps }">
              <div
                class="timeline-block"
                :class="{ 'timeline-block--manual': assignment.manual }"
                :style="{
                  left: `${blockLeft(assignment.arrivalTime)}%`,
                  width: `${blockWidth(assignment.arrivalTime, assignment.departureTime)}%`,
                  backgroundColor: colorMap[cgId] || '#1976D2',
                }"
                v-bind="tooltipProps"
                @click.stop
              >
                {{ patientInitials(assignment.patientFullName) }}
              </div>
            </template>
            <span class="tooltip-text">{{ tooltipText(assignment) }}</span>
          </v-tooltip>
        </template>

        <!-- Lunch break block -->
        <v-tooltip
          v-if="getBreakStyle(cgId)"
          location="top"
        >
          <template #activator="{ props: tooltipProps }">
            <div
              class="timeline-block timeline-block--break"
              :style="getBreakStyle(cgId)!"
              v-bind="tooltipProps"
            >
              <v-icon
                size="10"
                color="white"
              >
                mdi-silverware-fork-knife
              </v-icon>
            </div>
          </template>
          <span>{{ t('routePlan.lunchBreak', { duration: breakDurationLabel(cgId) }) }}</span>
        </v-tooltip>
      </div>
    </div>
  </div>
</template>

<style scoped>
.tooltip-text {
  white-space: pre-line;
}

.route-timeline {
  --label-width: 120px;

  width: 100%;
  overflow-x: auto;
}

/* Days extending past 21:00 widen the rows proportionally so blocks keep their
   default scale and the container scrolls; ratio 1 keeps the full-width layout. */

.timeline-header,
.timeline-row {
  min-width: calc(var(--label-width) + (100% - var(--label-width)) * var(--timeline-extend-ratio, 1));
}

.timeline-header {
  display: flex;
  border-bottom: 1px solid #e0e0e0;
  padding: 4px 0;
  background: #fff;
}

.timeline-label {
  width: var(--label-width);
  flex-shrink: 0;
  font-size: 12px;
  padding: 4px 8px;

  /* Keep caregiver names readable while the track scrolls horizontally. */
  position: sticky;
  left: 0;
  z-index: 1;
  background: inherit;
}

.timeline-track {
  flex: 1;
  position: relative;
  height: 36px;
}

.timeline-block {
  position: absolute;
  top: 4px;
  height: 28px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  color: #fff;
  font-weight: bold;
  cursor: pointer;
  overflow: hidden;
}

.timeline-block--travel {
  top: 10px;
  height: 16px;
  background: repeating-linear-gradient(45deg,
  #e0e0e0,
  #e0e0e0 2px,
  transparent 2px,
  transparent 6px);
  display: flex;
  align-items: center;
  justify-content: center;
}

.timeline-block--existing {
  top: 4px;
  height: 28px;
  background: #bdbdbd;
  border-radius: 4px;
  opacity: .5;
  cursor: default;
}

/* Visit placed or moved by hand (not by the optimizer): dashed outline. */
.timeline-block--manual {
  border: 2px dashed rgb(255 255 255 / 90%);
}

/* Existing shift the optimizer couldn't place: a warning outline so it stands out. */
.timeline-block--dropped {
  background: #fb8c00;
  border: 2px solid #e65100;
  opacity: .9;
}

.timeline-block--break {
  top: 6px;
  height: 24px;
  background: #ffb74d;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: .7;
  cursor: default;
}

.timeline-row {
  display: flex;
  align-items: center;
  border-bottom: 1px solid #f5f5f5;
  cursor: pointer;
  background: #fff;
}

.timeline-row:hover {
  background: #f5f5f5;
}

.timeline-row--selected {
  background: #e3f2fd;
}

.timeline-hours {
  flex: 1;
  display: flex;
  justify-content: space-between;
  font-size: 11px;
  color: #666;
  padding: 0 4px;
}
</style>
